import {
  defineQuery,
  type Query,
  type QueryDefinition,
  type ReadonlyJSONValue,
} from "@rocicorp/zero";
import type { Context } from "./authorization";
import { schema } from "./generatedDrizzleSchema";
import type { Schema } from "./generatedDrizzleSchema";

// Synced queries are the tenant-isolation boundary: zero-cache serves every
// tenant from one replica, and /zero/query applies nothing beyond a query's own
// filters. defineTenantQuery is defineQuery that appends a tenantId filter to
// whatever query the function returns, so the tenant scope cannot be forgotten
// or bypassed (checks/tenant-scoped-queries.ts bans raw defineQuery in
// services/shared/queries.ts). Only the root is scoped; subqueries come from
// schema relationships, which join on parent keys.
function scopeToTenant<TQuery>(query: TQuery, ctx: Context): TQuery {
  const q = query as unknown as {
    ast: { table: string };
    where: (field: string, value: string) => unknown;
  };
  // The tenant registry has no tenantId column — a row's own id is the scope.
  if (q.ast.table === "tenants") return q.where("id", ctx.tenantId) as TQuery;
  const table = (
    schema.tables as Record<string, { columns: Record<string, unknown> }>
  )[q.ast.table];
  if (!table || !("tenantId" in table.columns)) {
    throw new Error(
      `Table "${q.ast.table}" has no replicated tenantId column, so it cannot root a tenant query yet (see TENANT_ID_SYNCED_TABLES in services/db/nonReplicatedColumns.ts)`
    );
  }
  return q.where("tenantId", ctx.tenantId) as TQuery;
}

// Zero's validator type (StandardSchemaV1) comes from @standard-schema/spec,
// which is not a direct dependency — recover it from the exported
// QueryDefinition instead of deep-importing the transitive package.
type StandardValidator<
  TInput extends ReadonlyJSONValue | undefined,
  TOutput extends ReadonlyJSONValue | undefined,
> = NonNullable<
  QueryDefinition<string, TInput, TOutput, unknown, Context>["validator"]
>;

type TenantQueryFunction<
  TTable extends keyof Schema["tables"] & string,
  TArgs extends ReadonlyJSONValue | undefined,
  TReturn,
> = (options: { args: TArgs; ctx: Context }) => Query<TTable, Schema, TReturn>;

export function defineTenantQuery<
  TInput extends ReadonlyJSONValue | undefined,
  TReturn,
  TTable extends keyof Schema["tables"] & string = keyof Schema["tables"] &
    string,
>(
  queryFn: TenantQueryFunction<TTable, TInput, TReturn>
): QueryDefinition<TTable, TInput, TInput, TReturn, Context>;
export function defineTenantQuery<
  TInput extends ReadonlyJSONValue | undefined,
  TOutput extends ReadonlyJSONValue | undefined,
  TReturn,
  TTable extends keyof Schema["tables"] & string = keyof Schema["tables"] &
    string,
>(
  validator: StandardValidator<TInput, TOutput>,
  queryFn: TenantQueryFunction<TTable, TOutput, TReturn>
): QueryDefinition<TTable, TInput, TOutput, TReturn, Context>;
export function defineTenantQuery(
  validatorOrFn: unknown,
  maybeFn?: unknown
): unknown {
  const wrap =
    (fn: TenantQueryFunction<never, never, never>) =>
    (options: { args: never; ctx: Context }) =>
      scopeToTenant(fn(options), options.ctx);
  return maybeFn
    ? defineQuery(
        validatorOrFn as StandardValidator<never, never>,
        wrap(maybeFn as TenantQueryFunction<never, never, never>)
      )
    : defineQuery(
        wrap(validatorOrFn as TenantQueryFunction<never, never, never>)
      );
}
